/**
 * Required contact-channel linking (#558) — which channels (Telegram first) are required for which employees,
 * employment-relationships, or the whole company, plus the manager-facing per-employee link-state overview.
 * Mirrors the monolith's `ContactLinkRequirement` (API.Core/Models/Company) and the manager API it is reached
 * through, `/company/contact-requirement` (ShardAdmin / CompanyOwner / PlatformOwner).
 *
 * **Wire format: every enum here is carried as its camelCase STRING name**, not a number — a deliberate
 * contract decision for the #558 endpoints specifically (coordinated with the monolith controllers and the
 * employee-side SPA), and NOT the same convention Forms' `BackfillPolicy`/`GateVerdict` use (those stay
 * numeric, untouched). Do not import Forms' numeric enums here, and do not import these string types into
 * Forms — the two features intentionally disagree on this, so a shared type would be a trap for whichever side
 * changes next.
 *
 * Each type below is a plain string-literal union (the wire value's own type), paired with a `const` object of
 * the same name holding its members — the same identifier resolves as a type in type position and as a value
 * in expression position, so every existing call site (`Policy.Grace`, `TargetType.Employee`, …) reads exactly
 * like it would against a `enum`, while the value that ships is unambiguously the string.
 */

import {GRACE_DAYS_DEFAULT, GRACE_DAYS_MAX, GRACE_DAYS_MIN, isValidGraceDays} from '../../../forms/shared/models/form-requirement.model';

// Re-exported so a consumer of this file can read the shared bounds without a second import — `graceDays` and
// `recheckIntervalDays` are plain numbers on the wire (never enums), so reusing the Forms bounds/validator here
// is unaffected by the string-vs-numeric enum decision above; it is the same `RequirementGraceDays` on both.
export {GRACE_DAYS_DEFAULT, GRACE_DAYS_MAX, GRACE_DAYS_MIN, isValidGraceDays};

/**
 * Who a contact requirement is hooked to. Mirrors `CheqMe.Server.Contracts.Requirements.RequirementTargetType`
 * — the same target-type vocabulary the Forms service's requirements use, plus the third member #558 added
 * (`AllEmployees`, which `FormRequirement` refuses outright).
 */
export type ContactRequirementTargetType = 'employee' | 'employmentRelationship' | 'allEmployees';
export const ContactRequirementTargetType = {
  /** Required for one specific employee. */
  Employee: 'employee',
  /** Required for everyone in an employment-relationship (job class) — current and future members. */
  EmploymentRelationship: 'employmentRelationship',
  /** Required for every employee of the company — current and future. */
  AllEmployees: 'allEmployees'
} as const satisfies Record<string, ContactRequirementTargetType>;

/**
 * A contact channel an employee can link and an employer can require. Mirrors
 * `CheqMe.Server.Contracts.Contact.ContactChannel`. Telegram is the only member in v1; WhatsApp/SMS are the
 * next candidates and land here as new members plus one catalog entry in `contact-channels.ts` — see that
 * file's docs for exactly what a new channel touches.
 */
export type ContactChannel = 'telegram';
export const ContactChannel = {
  Telegram: 'telegram'
} as const satisfies Record<string, ContactChannel>;

/**
 * What happens to an employee who does not yet have the required channel linked. A requirement carries two
 * independent policies, one per cohort — see {@link ContactRequirementResource}. Mirrors the backend
 * `BackfillPolicy` (`CheqMe.Server.Contracts.Requirements`) — the same TYPE Forms' requirements use — but
 * carried here as its string name rather than Forms' numeric convention (see the header comment).
 */
export type ContactRequirementPolicy = 'immediate' | 'grace';
export const ContactRequirementPolicy = {
  /** Locked out (verdict `restricted`) at the next login until the channel is linked. */
  Immediate: 'immediate',
  /** Kept working for the requirement's grace window (verdict `review`); `restricted` only once it has passed. */
  Grace: 'grace'
} as const satisfies Record<string, ContactRequirementPolicy>;

/**
 * The contact gate's overall verdict for one employee. Mirrors the backend `GateVerdict` — the SAME type
 * `ContactGateEvaluator` deliberately reuses from the required-forms gate ("the same shape for both gates") —
 * but again carried as its string name here rather than Forms' numeric convention.
 */
export type ContactGateVerdict = 'ok' | 'review' | 'restricted';
export const ContactGateVerdict = {
  /** All required channels linked and current → full access. */
  Ok: 'ok',
  /** A missing link or a due re-check is inside its grace window → warn + time-box. */
  Review: 'review',
  /** A required channel is missing (no window, or the window elapsed) → restrict. */
  Restricted: 'restricted'
} as const satisfies Record<string, ContactGateVerdict>;

/** Re-check bounds are the same `RequirementGraceDays` bounds a grace window uses (`ContactLinkRequirement.IsValidRecheckInterval`). */
export const RECHECK_INTERVAL_MIN = GRACE_DAYS_MIN;
/** @inheritDoc RECHECK_INTERVAL_MIN */
export const RECHECK_INTERVAL_MAX = GRACE_DAYS_MAX;

/**
 * Whether `days` is a re-check interval the server accepts: `null`/`undefined` (never re-check), or an integer
 * inside the same bounds a grace window uses. Mirrors `ContactLinkRequirement.IsValidRecheckInterval`.
 */
export function isValidRecheckIntervalDays(days: number | null | undefined): boolean {
  if (days === null || days === undefined) return true;
  return isValidGraceDays(days);
}

/** German label for a {@link ContactRequirementPolicy}, as shown in both cohort dropdowns. */
export function contactRequirementPolicyLabel(policy: ContactRequirementPolicy): string {
  return policy === ContactRequirementPolicy.Grace ? 'Frist gewähren' : 'Sofort sperren';
}

/**
 * What the existing-staff policy means for a *contact* requirement — worded around the channel rather than a
 * form, unlike the shared {@link ContactRequirementPolicy} label helpers in Forms' model, which name "das
 * Formular".
 */
export function existingStaffContactPolicyHint(policy: ContactRequirementPolicy, graceDays: number): string {
  return policy === ContactRequirementPolicy.Grace
    ? `Diese Mitarbeiter arbeiten noch ${graceDays} Tage weiter, bis zu einem gemeinsamen Stichtag.`
    : 'Diese Mitarbeiter werden ab der nächsten Anmeldung gesperrt, bis der Kontakt verknüpft ist.';
}

/** The new/returning-cohort counterpart of {@link existingStaffContactPolicyHint} — always relative, never a date. */
export function newStaffContactPolicyHint(policy: ContactRequirementPolicy, graceDays: number): string {
  return policy === ContactRequirementPolicy.Grace
    ? `Jede dieser Personen hat ${graceDays} Tage ab dem eigenen Eintritt — es gibt kein gemeinsames Datum.`
    : 'Diese Mitarbeiter werden ab der ersten Anmeldung nach ihrem Eintritt gesperrt, bis der Kontakt verknüpft ist.';
}

/** What a re-check interval means, for the hint line under its field. `null` reads as "keine erneute Prüfung". */
export function recheckIntervalHint(days: number | null | undefined): string {
  return days == null
    ? 'Die Verknüpfung wird nach dem Verknüpfen nicht erneut geprüft.'
    : `Der Kontakt muss die Verknüpfung alle ${days} Tage erneut bestätigen; eine überfällige Bestätigung wird wie eine fehlende Verknüpfung behandelt.`;
}

/** A required contact channel as returned to a manager. */
export interface ContactRequirementResource {
  id: string;
  channel: ContactChannel;
  targetType: ContactRequirementTargetType;
  employeeId?: string | null;
  employmentRelationshipId?: string | null;
  /**
   * The target's display name, resolved **server-side** — unlike the Forms requirement list, this screen never
   * has to cross-reference an employee/relationship roster to render a row. `"Alle Mitarbeiter"` for
   * {@link ContactRequirementTargetType.AllEmployees}.
   */
  targetName: string;
  start?: string | null;
  end?: string | null;
  mandatory: boolean;
  existingStaffPolicy: ContactRequirementPolicy;
  newStaffPolicy: ContactRequirementPolicy;
  graceDays: number;
  /** Every how many days the channel must re-confirm the identity; `null`/absent = never. */
  recheckIntervalDays?: number | null;
  effectiveFrom: string;
  /** The shared existing-staff deadline; `null` when that cohort's policy is `immediate`. */
  existingStaffDeadline?: string | null;
  created: string;
}

/** Create a contact requirement: hook a channel to a target. */
export interface CreateContactRequirementRequest {
  channel: ContactChannel;
  targetType: ContactRequirementTargetType;
  /** Set for {@link ContactRequirementTargetType.Employee} only. */
  employeeId?: string;
  /** Set for {@link ContactRequirementTargetType.EmploymentRelationship} only. */
  employmentRelationshipId?: string;
  start?: string | null;
  end?: string | null;
  mandatory: boolean;
  existingStaffPolicy: ContactRequirementPolicy;
  newStaffPolicy: ContactRequirementPolicy;
  graceDays: number;
  recheckIntervalDays?: number | null;
}

/**
 * Change an existing requirement's gating settings. Deliberately narrower than the create request: the channel
 * and the target are immutable server-side (re-pointing either would move the effective-from date every
 * deadline hangs off) — re-targeting is delete plus create. Unlike the Forms requirement update, this one does
 * **not** carry `start`/`end`: `ContactLinkRequirement`'s update surface leaves the temporal validity untouched.
 */
export interface UpdateContactRequirementRequest {
  mandatory: boolean;
  existingStaffPolicy: ContactRequirementPolicy;
  newStaffPolicy: ContactRequirementPolicy;
  graceDays: number;
  recheckIntervalDays?: number | null;
}

/**
 * The gating settings while a manager is editing them — shared by the "new assignment" card and the in-place
 * row editor, so both render from one definition and one set of validation rules. `graceDays`/`recheckIntervalDays`
 * are nullable **here only**: a number input reports an empty field as `null`, and that is what tells "not filled
 * in yet" apart from a real value. Neither is ever sent as `null` for `graceDays` — the server has no "no grace
 * window" reading — but `recheckIntervalDays: null` is a real, meaningful request value ("never").
 */
export interface ContactRequirementGatingDraft {
  existingStaffPolicy: ContactRequirementPolicy;
  newStaffPolicy: ContactRequirementPolicy;
  graceDays: number | null;
  recheckIntervalDays: number | null;
}

/** A fresh draft with the server's own defaults — both cohorts `immediate`, the 14-day window, no re-check. */
export function defaultGatingDraft(): ContactRequirementGatingDraft {
  return {
    existingStaffPolicy: ContactRequirementPolicy.Immediate,
    newStaffPolicy: ContactRequirementPolicy.Immediate,
    graceDays: GRACE_DAYS_DEFAULT,
    recheckIntervalDays: null
  };
}

/** The draft a manager starts from when they open an existing requirement for editing. */
export function gatingDraftOf(requirement: ContactRequirementResource): ContactRequirementGatingDraft {
  return {
    existingStaffPolicy: requirement.existingStaffPolicy,
    newStaffPolicy: requirement.newStaffPolicy,
    graceDays: requirement.graceDays,
    recheckIntervalDays: requirement.recheckIntervalDays ?? null
  };
}

// ----- the manager overview (`/company/contact-requirement/overview`) ---------------------------------------

/** One channel's link state for one employee, as shown to a manager. */
export interface ContactRequirementChannelStatus {
  channel: ContactChannel;
  linked: boolean;
  lastVerifiedUtc?: string | null;
  /** The deadline this employee's own gate carries for this channel, if any is currently running. */
  deadline?: string | null;
}

/** One employee's overall contact-gate verdict plus the per-channel detail behind it. */
export interface ContactRequirementOverviewEntry {
  employeeId: string;
  name: string;
  verdict: ContactGateVerdict;
  channels: ContactRequirementChannelStatus[];
}

/** German label for a {@link ContactGateVerdict}, as shown in the overview's status pill. */
export function contactGateVerdictLabel(verdict: ContactGateVerdict): string {
  switch (verdict) {
    case ContactGateVerdict.Ok:
      return 'In Ordnung';
    case ContactGateVerdict.Review:
      return 'Frist läuft';
    case ContactGateVerdict.Restricted:
      return 'Gesperrt';
  }
}
